import React, { useState, useEffect } from 'react';
import { gsap } from "gsap";
import { useGSAP } from "@gsap/react";
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Loader from '../../components/Global/Loader';
import PasswordInput from '../../components/Global/PasswordInput';
import { fetchKanbanBoards } from '../../services/crmApi';

const countries = [
  "Afghanistan", "Albania", "Algeria", "Australia", "Bangladesh", "Belgium",
  "Brazil", "Canada", "China", "Denmark", "Egypt", "France", "Germany",
  "India", "Indonesia", "Iran", "Iraq", "Italy", "Japan", "Malaysia",
  "Mexico", "Netherlands", "New Zealand", "Pakistan", "Philippines",
  "Russia", "Saudi Arabia", "Singapore", "South Korea", "Spain",
  "Sweden", "Switzerland", "Thailand", "Turkey", "UAE",
  "United Kingdom", "United States", "Vietnam"
];

function AddConsultant() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);

  // Sales Person Role — create mode
  const [grantSalesRole, setGrantSalesRole] = useState(false);
  const [createBoardIds, setCreateBoardIds] = useState([]);
  const [createBoardError, setCreateBoardError] = useState(null);

  // Sales Person Role — edit mode (separate save action)
  const [salesRoleEnabled, setSalesRoleEnabled] = useState(false);
  const [salesBoardIds, setSalesBoardIds] = useState([]);
  const [salesRoleInitialized, setSalesRoleInitialized] = useState(false);
  const [salesRoleLoading, setSalesRoleLoading] = useState(false);
  const [salesRoleError, setSalesRoleError] = useState(null);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);

  // Shared board list
  const [availableBoards, setAvailableBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '', // Optional now
    password: '', // Optional now
    confirmPassword: '', // Optional now
    cellPhone: '',
    cnic: '',
    countryOfBirth: '',
    gender: '',
    maritalStatus: '',
    religion: '',
    dateOfBirth: '', // Changed from dob to match model
    program: '',
    major: '',
    universityName: '',
    campusCity: '',
    startDate: '',
    endDate: '',
    profileURL: null, //
    profilePicture: null, //
  });

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (file.size > maxSize) {
      alert("File size should not exceed 2MB");
      return;
    }

    if (file && ["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
      setSelectedImage(file);
      setFormData((prev) => ({ ...prev, profilePicture: file }));

      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    } else {
      alert("Please select a valid image file (JPEG, PNG, or JPG)");
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get("id");
    // console.log('Consultant ID:', id);

    if (id) {
      setIsEditing(true);
      fetchConsultantData(id);
    }
  }, [location]);

  const fetchConsultantData = async (id) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/consultant?consultantId=${id}`, // Updated endpoint
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // console.log("API Response:", response.data.data);
      if (response.data.data) {
        // Format dates before setting in form
        const consultantData = {
          ...response.data.data,
          dateOfBirth: response.data.data.dateOfBirth ? new Date(response.data.data.dateOfBirth).toISOString().split('T')[0] : '',
          startDate: response.data.data.startDate ? new Date(response.data.data.startDate).toISOString().split('T')[0] : '',
          endDate: response.data.data.endDate ? new Date(response.data.data.endDate).toISOString().split('T')[0] : '',
          password: response.data.data.password ? response.data.data.password : '',
        };
        setFormData(consultantData);
      }
    } catch (error) {
      // console.error("Error fetching consultant:", error);
      setError(error.response?.data?.message || "Failed to fetch consultant data");
    } finally {
      setLoading(false);
    }
  };

  const fetchBoards = async () => {
    try {
      setBoardsLoading(true);
      const data = await fetchKanbanBoards();
      setAvailableBoards(data.data || []);
    } catch (_) {
      setAvailableBoards([]);
    } finally {
      setBoardsLoading(false);
    }
  };

  // Initialize edit-mode sales role state once consultant data is loaded.
  // Use formData.email as the "data has arrived" signal because the API always
  // returns it, whereas hasSalesPersonRole may be absent on older records.
  useEffect(() => {
    if (isEditing && !salesRoleInitialized && formData.email) {
      setSalesRoleEnabled(!!formData.hasSalesPersonRole);
      setSalesBoardIds(Array.isArray(formData.assignedBoardIds) ? formData.assignedBoardIds : []);
      setSalesRoleInitialized(true);
      if (formData.hasSalesPersonRole) fetchBoards();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.email, formData.hasSalesPersonRole, formData.assignedBoardIds, isEditing, salesRoleInitialized]);

  const saveSalesRole = async () => {
    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get("id");
    if (!id) return;

    // If revoking, show confirmation first
    const wasEnabled = formData.hasSalesPersonRole;
    if (wasEnabled && !salesRoleEnabled && !showRevokeConfirm) {
      setShowRevokeConfirm(true);
      return;
    }

    try {
      setSalesRoleLoading(true);
      setSalesRoleError(null);
      const body = { hasSalesPersonRole: salesRoleEnabled };
      if (salesRoleEnabled) body.assignedBoardIds = salesBoardIds;

      await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/admin/consultants/${id}/sales-role`,
        body,
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );

      toast.success("Sales person role updated");
      setFormData(prev => ({ ...prev, hasSalesPersonRole: salesRoleEnabled, assignedBoardIds: salesBoardIds }));
    } catch (error) {
      if (error.response?.status === 400) {
        setSalesRoleError(error.response.data.message);
      } else if (error.response?.status === 404) {
        toast.error("Consultant not found");
      } else {
        toast.error("Failed to update sales role. Please try again.");
      }
    } finally {
      setSalesRoleLoading(false);
      setShowRevokeConfirm(false);
    }
  };

  useGSAP(() => {
    const fields = document.querySelectorAll(".relative");

    fields.forEach((field) => {
      const input = field.querySelector("input, select");
      const label = field.querySelector("label");
      const pLabel = field.querySelector("p");

      if (!input || !label) {
        // console.warn("Missing input or label in field:", field);
        return;
      }

      // Set initial state based on whether field has a value
      const hasValue = input.value && input.value.trim() !== '';

      gsap.set(label, {
        top: hasValue ? -10 : 10,
        fontSize: hasValue ? "0.70rem" : "1rem",
        color: "#6b7280"
      });

      if (hasValue) {
        input.classList.remove("error-border");
        if (pLabel) {
          pLabel.classList.remove("text-red-500");
          pLabel.classList.add("text-gray-500");
        }
      }

      // Focus event handler
      const handleFocus = () => {
        gsap.to(label, {
          top: -10,
          fontSize: "0.70rem",
          color: "#22c55e",
          duration: 0.25
        });
        gsap.to(input, { borderColor: "#22c55e", duration: 0.25 });
        if (pLabel) {
          pLabel.classList.remove("text-red-500");
          pLabel.classList.add("text-gray-500");
        }
      };

      // Input/Change event handler
      const handleInputChange = () => {
        const hasInputValue = input.value && input.value.trim() !== '';
        if (hasInputValue) {
          gsap.to(label, {
            top: -10,
            fontSize: "0.70rem",
            color: "#22c55e",
            duration: 0.25
          });
          gsap.to(input, { borderColor: "#22c55e", duration: 0.25 });
          input.classList.remove("error-border");
        }
      };

      // Blur event handler
      const handleBlur = () => {
        const hasInputValue = input.value && input.value.trim() !== '';
        if (!hasInputValue) {
          gsap.to(label, {
            top: 10,
            fontSize: "1rem",
            color: "#6b7280",
            duration: 0.25
          });
          gsap.to(input, { borderColor: "#ef4444", duration: 0.25 });
          input.classList.add("error-border");
          if (pLabel) {
            pLabel.textContent = "Required *";
            pLabel.classList.remove("text-gray-500");
            pLabel.classList.add("text-red-500");
          }
        } else {
          input.classList.remove("error-border");
          gsap.to(input, { borderColor: "#6b7280", duration: 0.25 });
          gsap.to(label, { color: "#6b7280", duration: 0.25 });
          if (pLabel) {
            pLabel.textContent = `Please enter ${label.textContent.toLowerCase()}`;
            pLabel.classList.remove("text-red-500");
            pLabel.classList.add("text-gray-500");
          }
        }
      };

      // Add event listeners
      input.addEventListener("focus", handleFocus);
      input.addEventListener("input", handleInputChange);
      input.addEventListener("change", handleInputChange); // For select elements
      input.addEventListener("blur", handleBlur);

      // Return cleanup function
      return () => {
        input.removeEventListener("focus", handleFocus);
        input.removeEventListener("input", handleInputChange);
        input.removeEventListener("change", handleInputChange);
        input.removeEventListener("blur", handleBlur);
      };
    });
  }, [formData]);

  // Handle changes in any input field
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Add these functions at the top of your component
  const createNewConsultant = async (formDataToSend) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/auth/register/consultant`,
        formDataToSend,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem("token")}`,
            'Content-Type': 'multipart/form-data'
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const updateExistingConsultant = async (id, formDataToSend) => {
    // console.log(formDataToSend);

    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/consultant?consultantId=${id}`,
        formDataToSend,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem("token")}`,
            'Content-Type': 'multipart/form-data'
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  // Update your handleSubmit function
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      // Update validation to only check required fields
      if (!formData.firstName || !formData.lastName) {
        setError('Please fill all required fields');
        return;
      }

      // Only validate passwords if a password is provided
      if (formData.password && formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      // Create FormData object
      const formDataToSend = new FormData();

      // Append all form fields to FormData
      Object.keys(formData).forEach(key => {
        if (key === 'profilePicture' && formData[key]) {
          // Handle file upload
          formDataToSend.append('profilePicture', formData[key]);
        }
        // Skip confirmPassword, hasSalesPersonRole, assignedBoardIds (handled separately), and null/undefined values
        else if (
          key !== 'confirmPassword' &&
          key !== 'hasSalesPersonRole' &&
          key !== 'assignedBoardIds' &&
          formData[key] !== null &&
          formData[key] !== undefined
        ) {
          formDataToSend.append(key, formData[key]);
        }
      });

      const queryParams = new URLSearchParams(location.search);
      const id = queryParams.get("id");

      // Append sales role fields only when creating
      if (!id) {
        formDataToSend.append('hasSalesPersonRole', grantSalesRole.toString());
        if (grantSalesRole) {
          createBoardIds.forEach(boardId => formDataToSend.append('assignedBoardIds', boardId));
        }
      }

      let response;
      if (id) {
        response = await updateExistingConsultant(id, formDataToSend);
      } else {
        response = await createNewConsultant(formDataToSend);
      }

      if (response?.data?.success) {
        setSuccess(true);
      }
    } catch (error) {
      // console.error("API Error:", error);
      const msg = error.response?.data?.message || '';
      if (error.response?.status === 400 && msg.toLowerCase().includes('board')) {
        setCreateBoardError(msg);
      } else if (error.response?.status === 404) {
        setError('Endpoint not found. Please check API URL.');
      } else if (error.response?.status === 409) {
        setError('Email already exists. Please use a different email address.');
      } else if (error.response?.status === 500) {
        setError('Server error. Please check your input data and try again.');
      } else {
        setError(error.response?.data?.message || 'Failed to save consultant');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div id="main-content" className="relative pl-36 mt-16 w-full p-8 bg-[#f4f8fb]">
        {loading && <Loader />}
        {
          success && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
                {/* Modal Content */}
                <h2 className="text-lg font-semibold text-gray-800 mb-4">
                  Success!
                </h2>
                <h4 className="text-sm text-gray-600 mb-6">
                  Consultant has been {isEditing ? 'updated' : 'added'} successfully!
                </h4>
                <div className="flex justify-end space-x-4">
                  {/* Proceed Button */}
                  <button
                    onClick={() => {
                      setSuccess(false);
                      navigate('/admin/consultants');
                    }}
                    className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none"
                  >
                    Okay
                  </button>
                </div>
              </div>
            </div>
          )
        }
        {error && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
              {/* Modal Content */}
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                Something went wrong!
              </h2>
              <h4 className="text-sm text-gray-600 mb-6">
                We encountered an issue while processing your request. Please try
                again later. If the problem persists, contact support.
              </h4>
              <div className="flex justify-end space-x-4">
                {/* Proceed Button */}
                <button
                  onClick={() => setError(null)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none"
                >
                  Okay
                </button>
              </div>
            </div>
          </div>
        )}
        {/* <!-- Content Wrapper --> */}
        <div className="flex flex-wrap space-x-6">
          {/* <!-- Right Card --> */}
          <div className="px-10 py-6 bg-white rounded shadow-lg w-full">
            <form onSubmit={handleSubmit}> {/* Add form element */}
              {/* <!-- Personal Info Heading --> */}
              <h2 className="text-gray-800 text-lg font-semibold mb-4">Personal Info</h2>

              {/* <!-- Edit Picture Button --> */}
              <div>
                <input
                  type="file"
                  id="imageInput"
                  accept=".jpg,.jpeg,.png"
                  onChange={handleImageChange}
                  className="hidden"
                />
                <button
                  type="button" // Add this to prevent form submission
                  onClick={(e) => {
                    e.preventDefault(); // Prevent form submission
                    document.getElementById('imageInput').click();
                  }}
                  className="flex items-center text-gray-600 text-xs border border-gray-400 bg-gray-200 px-3.5 py-2 hover:bg-black hover:text-white"
                >
                  <i className="fas fa-edit mr-2"></i>
                  {'Edit Picture'}
                </button>
              </div>

              {/* <!-- User Image --> */}
              <div className="mb-6">
                <img src={previewUrl || formData.profileURL || "/Assets/blank_form_profile.png"} alt="User Profile" className="size-[122px] object-cover border border-gray-300" />
              </div>

              {/* <!-- First Row of Fields --> */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {/* <!-- Title --> */}
                <div className="relative">
                  <label htmlFor="title" className="absolute text-sm text-gray-500 transhtmlForm top-2 left-3 bg-white px-1">Title</label>
                  <select id="title" name="title" value={formData.title} onChange={handleChange} className="w-full p-2 border rounded">
                    <option value=""></option>
                    <option value="Mr.">Mr.</option>
                    <option value="Ms.">Ms.</option>
                    <option value="Dr.">Dr.</option>
                  </select>
                  <p className="text-gray-500 text-xs mt-1">Please select a title</p>
                </div>

                {/* <!-- First Name --> */}
                <div className="relative">
                  <label htmlFor="first-name" className="absolute text-sm text-gray-500 transhtmlForm top-2 left-3 bg-white px-1">First Name</label>
                  <input id="first-name" type="text" name='firstName' value={formData.firstName} onChange={handleChange} className="w-full p-2 border rounded" />
                  <p className="text-gray-500 text-xs mt-1">Please enter first name</p>
                </div>

                {/* <!-- Last Name --> */}
                <div className="relative">
                  <label htmlFor="last-name" className="absolute text-sm text-gray-500 transhtmlForm top-2 left-3 bg-white px-1">Last Name</label>
                  <input id="last-name" type="text" name='lastName' value={formData.lastName} onChange={handleChange} className="w-full p-2 border rounded" />
                  <p className="text-gray-500 text-xs mt-1">Please enter last name</p>
                </div>
              </div>

              {/* <!-- Second Row of Fields --> */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                

                {/* <!-- Marital Status --> */}
                <div className="relative">
                  <label htmlFor="marital-status" className="absolute text-sm text-gray-500 transhtmlForm top-2 left-3 bg-white px-1">Marital Status</label>
                  <select id="marital-status" name="maritalStatus" value={formData.maritalStatus} onChange={handleChange} className="w-full p-2 border rounded">
                    <option value=""></option>
                    <option value="Married">Married</option>
                    <option value="Single">Single</option>
                    <option value="Divorced/Separated">Divorced/Separated</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                  <p className="text-gray-500 text-xs mt-1">Please select marital status</p>
                </div>

                {/* <!-- Gender --> */}
                <div className="relative">
                  <label htmlFor="gender" className="absolute text-sm text-gray-500 transhtmlForm top-2 left-3 bg-white px-1">Gender</label>
                  <select id="gender" name="gender" value={formData.gender} onChange={handleChange} className="w-full p-2 border rounded">
                    <option value=""></option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  <p className="text-gray-500 text-xs mt-1">Please select gender</p>
                </div>
              </div>

              {/* <!-- Biographical InhtmlFormation Section --> */}
              <h2 className="text-gray-800 text-lg font-semibold mt-6 mb-4">Biographical Information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {/* <!-- Date of Birth --> */}
                <div className="relative">
                  <label htmlFor="dob" className="absolute text-sm text-gray-500 transhtmlForm top-2 left-3 bg-white px-1">DOB</label>
                  <input
                    id="dob"
                    type="date"
                    name='dateOfBirth'
                    value={formData.dateOfBirth}
                    onChange={handleChange} className={`w-full p-2 border rounded ${!formData.dateOfBirth && 'text-transparent'}`} />
                  <p className="text-gray-500 text-xs mt-1">Please select date of birth</p>
                </div>

                {/* <!-- Country of Birth --> */}
                <div className="relative">
                  <label htmlFor="country-of-birth" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">
                    Country of Birth
                  </label>
                  <select
                    id="country-of-birth"
                    name="countryOfBirth"
                    value={formData.countryOfBirth}
                    onChange={handleChange}
                    className="w-full p-2 border rounded"
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                  <p className="text-gray-500 text-xs mt-1">Please select country of birth</p>
                </div>

                {/* <!-- Religion --> */}
                <div className="relative">
                  <label htmlFor="religion" className="absolute text-sm text-gray-500 transhtmlForm top-2 left-3 bg-white px-1">Religion</label>
                  <select id="religion" name="religion" value={formData.religion} onChange={handleChange} className="w-full p-2 border rounded">
                    <option value=""></option>
                    <option value="Islam">Islam</option>
                    <option value="Hindu">Hindu</option>
                    <option value="Ahmadi">Ahmadi</option>
                    <option value="Christianity">Christianity</option>
                  </select>
                  <p className="text-gray-500 text-xs mt-1">Please select religion</p>
                </div>
              </div>

              {/* <!-- Education Section --> */}
              <h2 className="text-gray-800 text-lg font-semibold mt-6 mb-4">Education Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="relative">
                  <label htmlFor="program" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">
                    Program
                  </label>
                  <input
                    id="program"
                    type="text"
                    name="program"
                    value={formData.program}
                    onChange={handleChange}
                    className="w-full p-2 border rounded"
                  />
                  <p className="text-gray-500 text-xs mt-1">Please enter program details</p>
                </div>

                <div className="relative">
                  <label htmlFor="major" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">
                    Major
                  </label>
                  <input
                    id="major"
                    type="text"
                    name="major"
                    value={formData.major}
                    onChange={handleChange}
                    className="w-full p-2 border rounded"
                  />
                  <p className="text-gray-500 text-xs mt-1">Please enter major details</p>
                </div>

                <div className="relative">
                  <label htmlFor="universityName" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">
                    University Name
                  </label>
                  <input
                    id="universityName"
                    type="text"
                    name="universityName"
                    value={formData.universityName}
                    onChange={handleChange}
                    className="w-full p-2 border rounded"
                  />
                  <p className="text-gray-500 text-xs mt-1">Please enter university name</p>
                </div>

                <div className="relative">
                  <label htmlFor="campusCity" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">
                    Campus City
                  </label>
                  <input
                    id="campusCity"
                    type="text"
                    name="campusCity"
                    value={formData.campusCity}
                    onChange={handleChange}
                    className="w-full p-2 border rounded"
                  />
                  <p className="text-gray-500 text-xs mt-1">Please enter campus city</p>
                </div>

                <div className="relative">
                  <label htmlFor="startDate" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">
                    Start Date
                  </label>
                  <input
                    id="startDate"
                    type="date"
                    name="startDate"
                    value={formData.startDate || ''}
                    onChange={handleChange}
                    className={`w-full p-2 border rounded ${!formData.startDate && 'text-transparent'}`}
                    
                  />
                  <p className="text-gray-500 text-xs mt-1">Please select start date</p>
                </div>

                <div className="relative">
                  <label htmlFor="endDate" className="absolute text-sm text-gray-500 transform top-2 left-3 bg-white px-1">
                    End Date
                  </label>
                  <input
                    id="endDate"
                    type="date"
                    name="endDate"
                    value={formData.endDate || ''}
                    onChange={handleChange}
                    className={`w-full p-2 border rounded ${!formData.endDate && 'text-transparent'}`}
                  />
                  <p className="text-gray-500 text-xs mt-1">Please select end date</p>
                </div>
              </div>

              {/* <!-- Personal Section --> */}
              <h2 className="text-gray-800 text-lg font-semibold mt-6 mb-4">Personal Details</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {/* <!-- CNIC --> */}
                <div className="relative">
                  <label htmlFor="cnic" className="absolute text-sm text-gray-500 transhtmlForm top-2 left-3 bg-white px-1"> CNIC</label>
                  <input type="number" name='cnic' value={formData.cnic} onChange={handleChange} className="w-full p-2 border rounded" />
                  <p className="text-gray-500 text-xs mt-1">Please enter CNIC</p>
                </div>

                {/* <!-- current-password --> */}
                <div className="relative">
                  <label htmlFor="current-password" className="absolute text-sm text-gray-500 transhtmlForm top-2 left-3 bg-white px-1">Password </label>
                  <PasswordInput
                    id="current-password"
                    name='password'
                    autoComplete='new-password'
                    value={formData.password}
                    onChange={handleChange}
                    inputClassName="p-2 border rounded"
                  />
                  <p className="text-gray-500 text-xs mt-1">Please enter password (optional)</p>
                </div>

                {/* <!-- confirm-password --> */}
                <div className="relative">
                  <label htmlFor="confirm-password" className="absolute text-sm text-gray-500 transhtmlForm top-2 left-3 bg-white px-1">Confirm Password </label>
                  <PasswordInput
                    id="confirm-password"
                    name='confirmPassword'
                    autoComplete='new-password'
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    inputClassName="p-2 border rounded"
                  />
                  <p className="text-gray-500 text-xs mt-1">Please confirm password (optional)</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {/* <!-- cell phone number --> */}
                <div className="relative">
                  <label htmlFor="cellphone-number" className="absolute text-sm text-gray-500 transhtmlForm top-2 left-3 bg-white px-1"> Cell Phone Number</label>
                  <input id="cellphone-number" type="tel" name='cellPhone' value={formData.cellPhone} onChange={handleChange} className="w-full p-2 border rounded" />
                  <p className="text-gray-500 text-xs mt-1"> Please enter cell phone number </p>
                </div>

                {/* <!-- email --> */}
                <div className="relative">
                  <label htmlFor="email" className="absolute text-sm text-gray-500 transhtmlForm top-2 left-3 bg-white px-1">Email </label>
                  <input type="email" name='email' value={formData.email} onChange={handleChange} className="w-full p-2 border rounded" />
                  <p className="text-gray-500 text-xs mt-1">Please enter email (optional)</p>
                </div>
              </div>

              {/* Sales Person Role — create mode only */}
              {!isEditing && (
                <div className="mt-8 border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-gray-800 text-base font-semibold">Grant Sales Person Role (CRM Access)</h3>
                      <p className="text-gray-500 text-sm mt-0.5">Allow this consultant to access the CRM boards, leads, and activities.</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={grantSalesRole}
                        onChange={(e) => {
                          setGrantSalesRole(e.target.checked);
                          setCreateBoardError(null);
                          if (!e.target.checked) {
                            setCreateBoardIds([]);
                          } else if (availableBoards.length === 0) {
                            fetchBoards();
                          }
                        }}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                  </div>

                  {grantSalesRole && (
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Assign Boards <span className="text-gray-400 font-normal">(optional)</span></p>
                      {boardsLoading ? (
                        <p className="text-sm text-gray-500">Loading boards...</p>
                      ) : availableBoards.length === 0 ? (
                        <p className="text-sm text-gray-400 italic">No boards available. Create a board first.</p>
                      ) : (
                        <div className="border border-gray-300 rounded max-h-48 overflow-y-auto p-2">
                          {availableBoards.map(board => (
                            <label key={board._id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 cursor-pointer rounded">
                              <input
                                type="checkbox"
                                checked={createBoardIds.includes(board._id)}
                                onChange={(e) => {
                                  setCreateBoardIds(prev =>
                                    e.target.checked ? [...prev, board._id] : prev.filter(id => id !== board._id)
                                  );
                                  setCreateBoardError(null);
                                }}
                                className="rounded"
                              />
                              <span className="text-sm text-gray-700">{board.name}</span>
                            </label>
                          ))}
                        </div>
                      )}
                      {createBoardError && (
                        <p className="text-red-500 text-sm mt-1">{createBoardError}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* <!-- Actions --> */}
              <div className="flex justify-end items-start space-x-4 mt-10 mr-5">
                {/* <!-- Save & Close and Close buttons --> */}
                <button
                  type="submit"
                  className="bg-white text-blue-500 border border-blue-500 px-4 py-2 rounded hover:bg-blue-500 hover:text-white"
                >
                  Save & Close
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/admin/consultants')}
                  className="bg-white text-gray-500 border border-gray-500 px-4 py-2 rounded hover:bg-gray-500 hover:text-white"
                >
                  Close
                </button>
              </div>
            </form>
          </div>

          {/* Sales Person Role — edit mode, separate save action */}
          {isEditing && salesRoleInitialized && (
            <div className="px-10 py-6 bg-white rounded shadow-lg w-full mt-6">
              <h2 className="text-gray-800 text-lg font-semibold mb-1">Sales Person Role</h2>
              <p className="text-gray-500 text-sm mb-6">Manage CRM access for this consultant. This is saved independently from the profile form above.</p>

              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm font-medium text-gray-700">CRM Access</p>
                  <p className="text-xs text-gray-400 mt-0.5">Currently: <span className={salesRoleEnabled ? "text-green-600 font-medium" : "text-gray-500"}>{salesRoleEnabled ? "ON" : "OFF"}</span></p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={salesRoleEnabled}
                    onChange={(e) => {
                      setSalesRoleEnabled(e.target.checked);
                      setSalesRoleError(null);
                      if (!e.target.checked) {
                        setSalesBoardIds([]);
                      } else if (availableBoards.length === 0) {
                        fetchBoards();
                      }
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                </label>
              </div>

              {salesRoleEnabled && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-gray-700 mb-2">Board Assignments <span className="text-gray-400 font-normal">(optional)</span></p>
                  {boardsLoading ? (
                    <p className="text-sm text-gray-500">Loading boards...</p>
                  ) : availableBoards.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No boards available. Create a board first.</p>
                  ) : (
                    <div className="border border-gray-300 rounded max-h-48 overflow-y-auto p-2">
                      {availableBoards.map(board => (
                        <label key={board._id} className="flex items-center space-x-2 p-2 hover:bg-gray-50 cursor-pointer rounded">
                          <input
                            type="checkbox"
                            checked={salesBoardIds.includes(board._id)}
                            onChange={(e) => {
                              setSalesBoardIds(prev =>
                                e.target.checked ? [...prev, board._id] : prev.filter(id => id !== board._id)
                              );
                              setSalesRoleError(null);
                            }}
                            className="rounded"
                          />
                          <span className="text-sm text-gray-700">{board.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {salesRoleError && (
                    <p className="text-red-500 text-sm mt-1">{salesRoleError}</p>
                  )}
                </div>
              )}

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={saveSalesRole}
                  disabled={salesRoleLoading}
                  className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {salesRoleLoading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>}
                  <span>Save Changes</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Revoke confirmation dialog */}
      {showRevokeConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white w-full max-w-md mx-auto rounded-lg shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Remove CRM Access?</h2>
            <p className="text-sm text-gray-600 mb-6">
              This will remove CRM access from this consultant. Their existing consultant work is unaffected. Continue?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowRevokeConfirm(false)}
                className="px-4 py-2 text-gray-500 border border-gray-300 rounded hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={saveSalesRole}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Remove Access
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AddConsultant;




